import { useStore } from 'react-redux';
import useIsInteragencyChatEnabled from '@rsos/hooks/useIsInteragencyChatEnabled';
import {
  MAX_MUTED_CONVERSATIONS,
  selectMutedInteragencyConversationIds,
  useGetMutedInteragencyConversationsQuery,
  useUpdateMutedInteragencyConversationsMutation,
  type InteragencyMutesApiState,
} from './interagencyUserSettingsAPI';

interface InteragencyConversationSubscription {
  /** Whether the user is subscribed to the conversation passed to the hook. */
  isSubscribed: boolean;
  /**
   * True until the mute list has been fetched. `isSubscribed` stays a plain
   * boolean through that window — a read cannot be awaited from render, so the
   * pending state is reported rather than promised.
   */
  isLoading: boolean;
  subscribe: (conversationId: string) => Promise<void>;
  unsubscribe: (conversationId: string) => Promise<void>;
}

const EMPTY_MUTES: string[] = [];

/**
 * Writes are serialized: each one reads the current list and replaces it
 * wholesale, so two in flight at once would drop each other's ids.
 */
let pendingWrite: Promise<unknown> = Promise.resolve();

function enqueueWrite(run: () => Promise<void>): Promise<void> {
  // The catch comes BEFORE run: run executes whether the previous write
  // succeeded or failed, while a rejection from run itself still reaches the
  // caller. (A catch placed after run would re-execute a failed write.)
  const write = pendingWrite.catch(() => undefined).then(run);
  pendingWrite = write.catch(() => undefined);
  return write.catch((err: unknown) => {
    // Log every failed mute write centrally, whatever the caller does with the
    // rejection — a dropped write means the bell does not stick.
    // eslint-disable-next-line no-console -- we want these in Sentry
    console.error('Failed to persist interagency chat mutes:', err);
    throw err;
  });
}

function sameList(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((value, i) => value === b[i]);
}

/**
 * The single read/write surface for interagency chat subscriptions, which are
 * what gate notifications for a conversation. Nothing outside this module
 * should touch the mute endpoints directly.
 *
 * Subscription is opt-out: what gets stored is the set of conversations the
 * user has *muted*, so a user with nothing stored is subscribed everywhere and
 * notified by default. The hook still speaks in subscribe terms because that is
 * what the bell shows — `isSubscribed` is simply "not muted".
 *
 * Pass the conversation being displayed to read its state; callers that only
 * write can omit it and use the mutations alone.
 *
 * Mutes are stored per-user on the backend (scorpius user attributes), so they
 * survive reload and follow the user across devices. The list is ordered
 * oldest-mute-first and capped: muting past the cap evicts from the front.
 */
export function useInteragencyConversationSubscription(
  conversationId?: string | null,
): InteragencyConversationSubscription {
  const isChatEnabled = useIsInteragencyChatEnabled();
  const { data, isLoading } = useGetMutedInteragencyConversationsQuery(
    undefined,
    { skip: !isChatEnabled },
  );
  const [updateMutes] = useUpdateMutedInteragencyConversationsMutation();
  const store = useStore<InteragencyMutesApiState>();

  const mutedConversationIds = data ?? EMPTY_MUTES;
  const isSubscribed =
    !!conversationId && !mutedConversationIds.includes(conversationId);

  return {
    isSubscribed,
    isLoading,
    // Subscribing is unmuting: drop the id from the stored mute list.
    subscribe: id =>
      enqueueWrite(async () => {
        // Read at execution time, not render time — an earlier queued write
        // may have changed the list since this callback was created.
        const current = selectMutedInteragencyConversationIds(store.getState());
        const next = current.filter(otherId => otherId !== id);
        if (sameList(next, current)) return;
        await updateMutes({ mutedConversationIds: next }).unwrap();
      }),
    unsubscribe: id =>
      enqueueWrite(async () => {
        const current = selectMutedInteragencyConversationIds(store.getState());
        if (current.includes(id)) return;
        const next = [...current, id].slice(-MAX_MUTED_CONVERSATIONS);
        await updateMutes({ mutedConversationIds: next }).unwrap();
      }),
  };
}

/**
 * IDs of every conversation the user has muted. For consumers that decide
 * per-event (not per-render) whether a conversation is subscribed — notification
 * gating checks the conversation an inbound message belongs to, which is not
 * known until the message arrives. An id being absent means subscribed.
 */
export function useMutedInteragencyConversationIds(): string[] {
  const isChatEnabled = useIsInteragencyChatEnabled();
  const { data } = useGetMutedInteragencyConversationsQuery(undefined, {
    skip: !isChatEnabled,
  });
  return data ?? EMPTY_MUTES;
}
